import React from "react";
import { ArrowDownLeft, ArrowUpRight, Receipt } from "lucide-react";
import { Transaction } from "../../constants/dataTypes"; 

interface SummaryStats {
  credits: number;
  debits: number;
  count: number;
}

interface TransactionSummaryProps {
  transactions: Transaction[];
  pagination: {
    total: number;
  };
}

const TransactionSummary: React.FC<TransactionSummaryProps> = ({ 
  transactions, 
  pagination 
}) => {
  const formatCurrency = (amount: number, currency: string = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const calculateSummary = (): SummaryStats => {
    const credits = transactions
      .filter(t => t.transactionType === "credit")
      .reduce((sum, t) => sum + (t.amount - (t.amountRefunded || 0)), 0);
    
    const debits = transactions
      .filter(t => t.transactionType === "debit")
      .reduce((sum, t) => sum + (t.amount - (t.amountRefunded || 0)), 0);

    return { 
      credits, 
      debits, 
      count: pagination.total 
    };
  };

  const summary = calculateSummary();

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-300">Total Credits</p>
            <p className="text-2xl font-bold text-green-900 dark:text-green-200">
              {formatCurrency(summary.credits)}
            </p>
          </div>
          <ArrowDownLeft size={24} className="text-green-600 dark:text-green-400" />
        </div>
      </div>
      
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">Total Debits</p>
            <p className="text-2xl font-bold text-red-900 dark:text-red-200">
              {formatCurrency(summary.debits)}
            </p>
          </div>
          <ArrowUpRight size={24} className="text-red-600 dark:text-red-400" />
        </div>
      </div>
      
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Total Transactions</p>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">
              {summary.count}
            </p>
          </div>
          <Receipt size={24} className="text-blue-600 dark:text-blue-400" />
        </div>
      </div>
    </div>
  );
};

export default TransactionSummary;